// Clinical production features the Flutter console had and the React console restores
// (parity audit 2026-09-30, finish-1). Every route here is SERVED by the back end today
// (router.ts under /api/aithora, src/financial/router.ts under /api/financial).
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';

const A = AITHORA_PREFIX;

// ── Deterioration indicators (router.ts /*-risk-trajectory) ───────────────────

/** One wellness indicator's read (delirium-risk.ts, ade-risk.ts and their siblings). */
export interface RiskIndicator {
  residentId: string;
  /** 'disabled' while the indicator's flag is off; then nothing is scored. */
  state: 'scored' | 'building_baseline' | 'insufficient_data' | 'disabled';
  score: number | null;
  tier: 'WATCH' | 'ELEVATED' | 'HIGH' | null;
  confidence: 'low' | 'medium' | 'high';
  topContributors: Array<{ label: string; explanation: string; points: number }>;
  gate?: { validDays: number; requiredDays: number; message: string };
  recommendation: string | null;
  asOfDay: string | null;
  disclaimer: string;
}
type RiskQ = { userId: string; organizationId?: string };
export const deliriumRisk = route<RiskIndicator, RiskQ>()('GET', `${A}/delirium-risk-trajectory`);
export const pressureInjuryRisk = route<RiskIndicator, RiskQ>()('GET', `${A}/pressure-injury-risk-trajectory`);
export const cdiffRisk = route<RiskIndicator, RiskQ>()('GET', `${A}/cdiff-risk-trajectory`);
export const adeRisk = route<RiskIndicator, RiskQ>()('GET', `${A}/ade-risk-trajectory`);
export const isolationRisk = route<RiskIndicator, RiskQ>()('GET', `${A}/isolation-risk-trajectory`);

// ── Medication review (router.ts /medication-review) ──────────────────────────

export type MedReviewOutcome = 'pending' | 'discussed' | 'accepted' | 'declined';
export interface MedReviewCandidate {
  medicationId: string | null;
  medicationName: string;
  ruleId: string;
  reason: string;
  confidence: string;
  outcome: MedReviewOutcome;
  outcomeNote: string | null;
}
export interface MedicationReview {
  framing: { title: string; disclaimer: string; advisoryOnly: true };
  medicationCount: number;
  gated: boolean;
  gateReason?: string;
  candidates: MedReviewCandidate[];
}
/** GET /medication-review?userId=&organizationId=: to discuss with the doctor, never an instruction. */
export const medicationReview = route<MedicationReview, RiskQ>()('GET', `${A}/medication-review`);
/** POST /medication-review/outcome: what the doctor decided (Aithora never changes a medication). */
export const medicationReviewOutcome = route<
  { outcome: unknown },
  void,
  { userId: string; organizationId?: string; ruleId: string; medicationId?: string | null; outcome: MedReviewOutcome; note?: string }
>()('POST', `${A}/medication-review/outcome`);

// ── Reminders (router.ts /reminders) ──────────────────────────────────────────

export type ReminderKindName = 'medication' | 'appointment' | 'checkin' | 'custom';
export interface ReminderCreateBody {
  residentId: string;
  title: string;
  kind: ReminderKindName;
  channel?: 'push' | 'companion' | 'family';
  /** A one-off time, or a daily / weekly recurrence (HH:MM wall times). */
  fireAt?: string;
  recurrence?: { freq: 'daily' | 'weekly'; times: string[]; daysOfWeek?: number[] };
  timezone?: string;
}
/** POST /reminders: staff of the building, the resident, or linked family. Medication changes are refused (422). */
export const reminderCreate = route<{ reminder: { id: string } }, void, ReminderCreateBody>()('POST', `${A}/reminders`);
/** PATCH /reminders/:id {residentId, status}: pause, resume (active) or mark done. */
export const reminderSetStatus = route<{ reminder: { id: string; status: string } }, void, { residentId: string; status: 'active' | 'paused' | 'done' }>()(
  'PATCH', `${A}/reminders/:id`);
/** DELETE /reminders/:id?residentId=: cancel (kept on record, never fires again). */
export const reminderCancel = route<{ ok: boolean }, { residentId: string }>()('DELETE', `${A}/reminders/:id`);

// ── Wearables (router.ts /sensors/*) ──────────────────────────────────────────

export interface ResidentDevices {
  connected: Array<{ provider: string; connectedAt: string; via?: 'oauth' | 'junction'; lastReadingAt?: string | null }>;
  connectable: string[];
  /** Whether each path can run today (switches and keys), so a control can say why not. */
  ingestEnabled?: boolean;
  junctionConfigured?: boolean;
}
/** GET /sensors/devices?resident_id=: what is paired, when it last sent a reading, and what can be. */
export const residentDevices = route<ResidentDevices, { resident_id: string }>()('GET', `${A}/sensors/devices`);
/** GET /sensors/connect/:provider?resident_id=: the vendor's sign-in URL (503 until set up). */
export const deviceConnect = route<{ authorizeUrl: string }, { resident_id: string }>()('GET', `${A}/sensors/connect/:provider`);
/** POST /sensors/junction/link {resident_id}: Junction's hosted connect page (503 until the key is set). */
export const junctionLink = route<{ ok: boolean; linkUrl: string }, void, { resident_id: string }>()('POST', `${A}/sensors/junction/link`);
/** DELETE /sensors/devices/:provider?resident_id=: unpair. */
export const deviceDisconnect = route<{ ok: boolean; disconnected: string }, { resident_id: string }>()('DELETE', `${A}/sensors/devices/:provider`);

// ── SMART Health Card (router.ts /health-cards/*) ─────────────────────────────

export interface HealthCard {
  verifiableCredential: string[];
  rid: string;
  kid: string;
  issuer: string;
  ephemeralKey?: boolean;
}
/** GET /health-cards/:userId: issue a signed SMART Health Card (verifies offline). */
export const healthCardIssue = route<HealthCard>()('GET', `${A}/health-cards/:userId`);
/** POST /health-cards/revoke/:rid {reason}: revoke one issued card. */
export const healthCardRevoke = route<{ revoked: boolean; rid: string }, void, { reason?: string }>()('POST', `${A}/health-cards/revoke/:rid`);

// ── Frailty / CHESS and med adherence (src/financial/router.ts /facilities/mine/*) ──

export interface FacilityFrailty {
  org: string;
  residents: number;
  sufficient: boolean;
  chess: { mean: number; distribution: number[]; bands: string[]; highRisk: number };
  mrs3: { meanScore: number; highRisk: number; highRiskPct: number; meanItemsProvided: number };
}
/** GET /api/financial/facilities/mine/risk?org=: CHESS 0-5 and MRS3 from baseline MDS (own building; Aithora names one). */
export const facilityFrailty = route<FacilityFrailty, { org?: string }>()('GET', '/api/financial/facilities/mine/risk');
interface Iv { point: number; lower: number; upper: number; n: number; sufficient: boolean }
export interface FacilityCareOps {
  orgId: string;
  careOps: {
    medAdherence: {
      total: number; sufficient: boolean; given: number; refused: number; held: number; missed: number;
      givenPct: Iv; refusedPct: Iv; heldPct: Iv; missedPct: Iv;
      /** Of missed: medication-days that ended with nothing recorded. */
      undocumented?: number;
      /** Doses due today with nothing recorded yet (the Worklist med pass's still to give). */
      stillToGiveToday?: number;
    };
  };
}
/** GET /api/financial/facilities/mine/outcomes?org=: care operations over the trailing 90 days. */
export const facilityCareOps = route<FacilityCareOps, { org?: string }>()('GET', '/api/financial/facilities/mine/outcomes');
